import React, { Suspense, lazy, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import GiftRecipientForm from '@components/pages/Subscribe/Forms/GiftRecipientForm';
import SignupForm from '@components/pages/Subscribe/Forms/SignupForm';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { READ_ONLY_MODE, READ_ONLY_PHRASE } from '@src/env';
import { SelectedOffer } from '@utils/backend-api/subscribe';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/constants';
import { LeadType } from '@utils/types';

const queryClient = new QueryClient();

const PaymentForm = lazy(() => import('@components/pages/Subscribe/Forms/PaymentForm'));

export interface SubscriptionFormProps {
  buttonText: string;
  className?: string;
  formLocation: string;
  giftRecipientCustomerId?: string;
  isDualSubscribePage?: boolean;
  leadType: LeadType;
  presentedOfferCodes: string[];
  referringSubscriberGuid?: string;
  referringSubscriberName?: string;
  selectedOffer: SelectedOffer;
  showGiftRecipientForm?: boolean;
  stripeCustomerId?: string;
  subscriptionStartDate?: string;
}

const SubscriptionForm = (props: SubscriptionFormProps) => {
  const {
    user: { isYPEligible, stripeCustomerId },
  } = useSubscribeForm();
  const isYPPlan = props.selectedOffer.code.includes(YOUNG_PROFESSIONALS_PLAN);
  const [recipientStripeCustomerId, setRecipientStripeCustomerId] = useState<string | null>(null);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState<string | null>(null);

  // Show payment form only if the user has a stripe customer id and in case of a YP plan, we want to make sure they have a date of birth too
  const showPaymentForm =
    (props.showGiftRecipientForm && recipientStripeCustomerId) ||
    (!props.showGiftRecipientForm && stripeCustomerId && (!isYPPlan || (isYPPlan && isYPEligible)));

  return (
    <div className={props.className}>
      {READ_ONLY_MODE ? (
        <div className="warning mt-28">
          <h3>Purchases are temporarily unavailable</h3>
          Sorry, we&apos;re doing some necessary site maintenance now, and this feature isn&apos;t
          available. Please try again in {READ_ONLY_PHRASE || '10-15 minutes'}. Thanks for your
          patience!
        </div>
      ) : (
        <QueryClientProvider client={queryClient}>
          <SignupForm {...props} />
          {props.showGiftRecipientForm && (
            <GiftRecipientForm
              selectedOffer={props.selectedOffer}
              setGiftrecipientCustomerId={setRecipientStripeCustomerId}
              giftRecipientCustomerId={recipientStripeCustomerId}
              setSubscriptionStartDate={setSubscriptionStartDate}
              giverStripeCustomerId={stripeCustomerId}
            />
          )}
          <div className="m-auto max-w-md sm:max-w-full">
            <SectionBreakTitle
              title={
                props.showGiftRecipientForm ? '3. Payment information' : '2. Payment information'
              }
              titleClassName="text-black"
              color={props.selectedOffer.isPro ? SectionColor.Gold : SectionColor.Magenta}
              className="mb-24 mt-32"
            />
            {showPaymentForm && (
              <Suspense>
                <PaymentForm
                  {...props}
                  stripeCustomerId={stripeCustomerId}
                  giftRecipientCustomerId={recipientStripeCustomerId}
                  subscriptionStartDate={subscriptionStartDate}
                />
              </Suspense>
            )}
          </div>
        </QueryClientProvider>
      )}
    </div>
  );
};

export default SubscriptionForm;
